import { Navigation, NavigationItem } from '@globalmed/atlas-ui';
import {
  NavigationWithOrganizationsBody,
  NavigationWithOrganizationsHeader,
} from './components';
import { useInstitutionsStore } from '@/stores/institutions';
import { useOrganizationsStore } from '@/stores/organizations';
import { INavigationItemPlus } from '@/types';

type TProps = {
  items: INavigationItemPlus[];
  activeKey?: string;
  onChange: (item: NavigationItem) => void;
};

/**
 * Super-admin navigation panel with an organizations/institutions switcher.
 * Receives its nav items and active-item state from NavPanel instead of
 * computing them itself, so the report-config fetch in useNavigationItems
 * only ever runs once per NavPanel mount. The admin nav items list is
 * hidden while the current organization has no institution to operate on.
 * @param props - Component props
 * @param props.items - Translated navigation items to render
 * @param props.activeKey - Key of the currently active navigation item
 * @param props.onChange - Called when a navigation item is selected
 * @returns The super-admin navigation panel
 */
export const NavigationWithOrganizations = ({
  items,
  activeKey,
  onChange,
}: TProps) => {
  const organizations = useOrganizationsStore((state) => state.organizations);
  const currentInstitution = useInstitutionsStore(
    (state) => state.currentInstitution,
  );
  const hasNoInstitution = !!organizations.length && !currentInstitution;
  const hasNoItems = hasNoInstitution || !items.length;

  return (
    // Atlas UI's Navigation seeds its internal "active item" state from
    // `items[0]` once on mount and only patches it up later via an effect,
    // and only when an `activeKey` matches. Flipping `items` between empty
    // and populated in place therefore either crashes on the stale
    // (undefined) state or leaves the first item highlighted on a route that
    // has no nav item of its own. Remounting on that transition avoids both.
    <Navigation
      key={hasNoItems ? 'no-items' : 'has-items'}
      items={hasNoItems ? [] : items}
      onChange={onChange}
      activeKey={activeKey}
      hideCollapse
      childrenBody={<NavigationWithOrganizationsBody />}
    >
      <NavigationWithOrganizationsHeader />
    </Navigation>
  );
};
